import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Parser } from '@json2csv/plainjs';
import { saveAs } from 'file-saver';

// Types
interface RootState {
  app: {
    currentIncident: {
      id: string;
      name: string;
    };
  };
}

interface IncidentLogEntry {
  email_address: string;
  minutes_worked_in_words: string;
  incident_seat: string;
  location?: string;
  notes?: string;
}

export const useGetIncidentLogs = (incidentId: string, groupGuid: string) => {
  return useQuery({
    queryKey: ['incidentLogs', incidentId],
    queryFn: async () => {
      const response = await axios(config.apiGateway.getIncidentLogs, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    enabled: !!incidentId && !!groupGuid,
  });
};

export const useDownloadIncidentLogs = (
  incidentId: string,
  groupGuid: string
) => {
  const { data: incidentLogs } = useGetIncidentLogs(incidentId, groupGuid);
  const currentIncident = useSelector(
    (state: RootState) => state.app.currentIncident
  );

  return useMutation({
    mutationFn: async () => {
      if (!incidentLogs) {
        throw new Error('No logs available to download');
      }

      const incidentMembersLogs = incidentLogs.incidentMembersLogs.map(
        (e: IncidentLogEntry) => ({
          'email_address/name': e.email_address,
          hours_worked: e.minutes_worked_in_words,
          incident_seat: e.incident_seat,
          location: '',
          notes: '',
        })
      );

      const incidentNonMembersLogs = incidentLogs.incidentNonMembersLogs.map(
        (e: IncidentLogEntry) => ({
          'email_address/name': e.email_address,
          hours_worked: e.minutes_worked_in_words,
          incident_seat: e.incident_seat,
          location: e.location,
          notes: e.notes,
        })
      );

      const allLogs = [...incidentMembersLogs, ...incidentNonMembersLogs];

      if (allLogs.length === 0) {
        throw new Error('There are no records in this log.');
      }

      const fields = Object.keys(allLogs[0]);
      const opts = { fields };
      const parser = new Parser(opts);
      const csv = parser.parse(allLogs);

      const downloadDataBlob = new Blob([csv], {
        type: 'application/csv',
      });

      const fileName = `DICE ${currentIncident?.name || 'Incident'} logs.csv`;

      saveAs(downloadDataBlob, fileName);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to download logs');
    },
  });
};
